import { useEffect, useState } from 'react';

import { useSetAtom } from 'jotai';
import isFunction from 'lodash/isFunction';

import { queryClient } from '@/services/api';

import { setAuthCookieOptions } from '@/_generated/api-clients/content';
import { licenseeLocationAtom } from '@/atoms/licensee-location';
import { isColorModePreference, userPrefsAtom } from '@/atoms/userPrefs';

export type NativeMessage = {
  type: string;
} & Record<string, unknown>;

export const sendNativeMessage = (message: NativeMessage) => {
  const isReactNativeWebView = isFunction(
    window.ReactNativeWebView?.postMessage,
  );
  if (isReactNativeWebView) {
    window.ReactNativeWebView?.postMessage(JSON.stringify(message));
  }
};

export const useNativeListener = (
  listener: (ev: MessageEvent<unknown>) => unknown,
) => {
  useEffect(() => {
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [listener]);
};

export const useNativeToken = () => {
  const [token, setToken] = useState<string>();
  useEffect(() => {
    const listener = (
      message: MessageEvent<{ type: string; payload: string }>,
    ) => {
      if (message.data.type === 'accessToken') {
        setToken(message.data.payload);
      }
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);
  return token;
};

/** Payload for the "locationPicker" event sent from native. */
export interface LocationPickerEventPayload {
  /** Location from native. */
  location: string;
}

/** Subscribe to the "locationPicker" event coming from native.  */
export const useNativeLocationPickerListener = () => {
  const sendLicenseeLocationDispatch = useSetAtom(licenseeLocationAtom);

  useEffect(() => {
    const listener = (e: CustomEvent<LocationPickerEventPayload>) => {
      sendLicenseeLocationDispatch('setActive', {
        number: e.detail.location,
        hasInterimManager: false,
      });
    };

    document.addEventListener('locationPicker', listener as EventListener);
    return () =>
      document.removeEventListener('locationPicker', listener as EventListener);
  }, [sendLicenseeLocationDispatch]);
};

/** Listen for the "refreshPathwayCookie" event coming from native. */
export const useNativeRefreshPathwayCookieListener = () => {
  useEffect(() => {
    const listener = async () => {
      await queryClient.fetchQuery({
        ...setAuthCookieOptions({ throwOnError: true }),
      });
    };

    document.addEventListener('refreshPathwayCookie', listener);
    return () => document.removeEventListener('refreshPathwayCookie', listener);
  }, []);
};

/**
 * Adopts the native host's color mode preference.
 *
 * Native emits `sendThemeEvent` onLoad + when preference changes.
 * Listener updates user theme preference which handles updating use-color-mode on the DOM.
 */
export const useNativeThemeChangedListener = () => {
  const setUserPrefs = useSetAtom(userPrefsAtom);

  useEffect(() => {
    const listener = (e: Event) => {
      if (!(e instanceof CustomEvent)) return;

      const detail: unknown = e.detail;
      if (typeof detail !== 'object' || detail === null) return;
      if (!('theme' in detail)) return;

      const { theme } = detail;
      if (!isColorModePreference(theme)) return;
      setUserPrefs(prev => (prev.theme === theme ? prev : { ...prev, theme }));
    };

    document.addEventListener('nativeThemeChanged', listener);
    return () => document.removeEventListener('nativeThemeChanged', listener);
  }, [setUserPrefs]);
};
